import { useState, useEffect, useCallback, useRef } from 'react';
import { getDepositoNotaFiscalResumoPendencias, getDepositoNotasFiscais } from '@spark/services/deposito';
import { formatarCNPJ, toApiDate } from '@spark/utils/formatters';

const formatarDataComHora = (data) => {
  if (!data) return '';
  return new Date(data).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const normalizeEnderecoPart = (value) => (value === null || value === undefined ? '' : String(value).trim());

const formatarEnderecoDeposito = (endereco) => {
  if (!endereco) return '-';

  const codigoArmazem = normalizeEnderecoPart(endereco.armazem?.codigo);
  const bloco = normalizeEnderecoPart(endereco.bloco);
  const rua = endereco.rua ?? '';
  const nivel = endereco.nivel ?? '';
  const posicao = endereco.posicao ?? '';

  if (!codigoArmazem && !bloco && rua === '' && nivel === '' && posicao === '') {
    return '-';
  }

  return `${codigoArmazem}.${bloco}.${rua}.${nivel}.${posicao}`;
};

const normalizeFilters = (filtros) => {
  return Object.fromEntries(
    Object.entries(filtros).filter(([, value]) => value !== undefined && value !== null && value !== '').map(
      ([key, value]) => [key, value instanceof Date ? toApiDate(value) : value]
    )
  );
};

const mapDepositoNotaListItem = (item) => ({
  id: item.id,
  chaveNFe: item.chaveNFe || item.ChaveNFe || '',
  numeroNF: item.numeroNF || '',
  serieNF: item.serieNF || '',
  tipoNota: item.tipoNota ?? 1,
  descricaoDevolucao: item.descricaoDevolucao || '',
  motivoDevolucao: item.motivoDevolucao ?? null,
  numeroPedidoPaletizacao: item.numeroPedidoPaletizacao || '',
  numeroPedidoEtiquetagem: item.numeroPedidoEtiquetagem || '',
  cnpjCliente: formatarCNPJ(item.cnpjCliente || ''),
  nomeCliente: item.nomeCliente || '',
  tipoOperacao: item.tipoOperacao || '',
  tabelaPreco: item.tabelaPreco?.nomeTabelaPrecoDeposito || '',
  tabelaPrecoNome: item.tabelaPreco?.nomeTabelaPrecoDeposito || '',
  obsTabelaPreco: item.tabelaPreco?.observacoes || '',
  idDepositoTabelaPreco: item.idDepositoTabelaPreco ?? null,
  nomeDestinatario: item.nomeDestinatario || '',
  nomeRemetente: item.nomeRemetente || '',
  numeroCarga: item.numeroCarga || '',
  numeroPedido: item.numeroPedido || '',
  placaVeiculo: item.placaVeiculo || '',
  numeroFrota: item.numeroFrota || '',
  descricaoModeloVeicular: item.descricaoModeloVeicular || '',
  infoMotorista: item.infoMotorista || '',
  modelosReboque: item.modelosReboque || '',
  frotasReboque: item.frotasReboque || '',
  placasReboque: item.placasReboque || '',
  dataAgendamentoPedido: item.dataAgendamentoPedido ? formatarDataComHora(item.dataAgendamentoPedido) : '',
  dataAgendamentoPedidoOriginal: item.dataAgendamentoPedido || null,
  descricaoSituacaoDigitalizacaoCanhoto: item.descricaoSituacaoDigitalizacaoCanhoto || '',
  nomeUsuarioLeitura: item.nomeUsuarioLeitura || '',
  nomeResponsavelComercial: item.nomeResponsavelComercial || '',
  nomeResponsavelLancamento: item.nomeResponsavelLancamento || '',
  nomeResponsavelFechamento: item.nomeResponsavelFechamento || '',
  dataChegada: item.dataChegada ? formatarDataComHora(item.dataChegada) : '',
  dataAgendamento: item.dataAgendamento ? formatarDataComHora(item.dataAgendamento) : '',
  dataLeitura: item.dataLeitura ? formatarDataComHora(item.dataLeitura) : '',
  dataSaida: item.dataSaida ? formatarDataComHora(item.dataSaida) : '',
  dataChegadaOriginal: item.dataChegada || null,
  dataLeituraOriginal: item.dataLeitura || null,
  dataConferenciaOriginal: item.dataConferencia || null,
  dataArmazenagemOriginal: item.dataArmazenagem || null,
  dataAgendamentoOriginal: item.dataAgendamento || null,
  dataSaidaOriginal: item.dataSaida || null,
  dataEnvioCanhotoOriginal: item.dataEnvioCanhoto || null,
  dataUltimaCobrancaArmazenagemOriginal: item.dataUltimaCobrancaArmazenagem || null,
  quantidadeTotalPaletes: item.quantidadeTotalPaletes,
  quantidadeTotalEtiquetas: item.quantidadeTotalEtiquetas,
  quantidadeTotalCaixas: item.quantidadeTotalCaixas,
  quantidadePaletesCobranca: item.quantidadePaletesCobranca,
  quantidadeEtiquetasCobranca: item.quantidadeEtiquetasCobranca,
  idDepositoArmazenagemLocal: item.idDepositoArmazenagemLocal ?? null,
  idDepositoEndereco: item.idDepositoEndereco ?? null,
  armazenagemLocal: item.armazenagemLocal || null,
  endereco: item.endereco || null,
  enderecoFormatado: formatarEnderecoDeposito(item.endereco),
  localArmazenamento: item.armazenagemLocal?.descricao || '',
  armazemEndereco: item.endereco?.armazem?.nome || item.endereco?.armazem?.codigo || '',
  valorUnPallet: item.valorUnPallet,
  valorUnEtiqueta: item.valorUnEtiqueta,
  valorUnArmazenagemPallet: item.valorUnArmazenagemPallet,
  freetime: item.freetime,
  quantidadeDias: item.quantidadeDias ?? item.QuantidadeDias ?? null,
  diasExcedidos: item.diasExcedidos ?? item.DiasExcedidos ?? null,
  valorTotalPaletizacao: item.valorTotalPaletizacao,
  valorTotalEtiquetagem: item.valorTotalEtiquetagem,
  valorTotalArmazenagemPallet: item.valorTotalArmazenagemPallet,
  valorTotalEmitidoPaletizacao: item.valorTotalEmitidoPaletizacao,
  valorTotalEmitidoEtiquetagem: item.valorTotalEmitidoEtiquetagem,
  valorTotalEmitidoArmazenagem: item.valorTotalEmitidoArmazenagem,
  valorTotalPendentePaletizacao: item.valorTotalPendentePaletizacao ?? item.ValorTotalPendentePaletizacao ?? null,
  valorTotalPendenteEtiquetagem: item.valorTotalPendenteEtiquetagem ?? item.ValorTotalPendenteEtiquetagem ?? null,
  valorTotalPendenteArmazenagem: item.valorTotalPendenteArmazenagem ?? item.ValorTotalPendenteArmazenagem ?? null,
  usuarioEmissaoPaletizacao: item.usuarioEmissaoPaletizacao || '',
  usuarioEmissaoEtiquetagem: item.usuarioEmissaoEtiquetagem || '',
  protocoloPedidoTMSPaletizacao: item.protocoloPedidoTMSPaletizacao || '',
  protocoloPedidoTMSEtiquetagem: item.protocoloPedidoTMSEtiquetagem || '',
  observacoes: item.observacoes || '',
  mudancaTabelaPrecoManual: item.mudancaTabelaPrecoManual ?? false,
  urlImagemCanhoto: item.urlImagemCanhoto || '',
  nomeUsuarioEnvioCanhoto: item.nomeUsuarioEnvioCanhoto || '',
  cobrancaPaletizacaoRealizada: item.cobrancaPaletizacaoRealizada,
  cobrancaEtiquetagemRealizada: item.cobrancaEtiquetagemRealizada,
  cobrancaArmazenagemRealizada: item.cobrancaArmazenagemRealizada,
  cobrancaArmazenagemPendente: item.cobrancaArmazenagemPendente ?? false,
  existeDivergencia: item.existeDivergencia,
  descricaoDivergencia: item.descricaoDivergencia || '',
  pendencias: Array.isArray(item.pendencias) ? item.pendencias : [],
  pendenciasTexto: item.pendenciasTexto || '',
  finalizado: item.finalizado ?? false,
  usuarioFinalizacao: item.usuarioFinalizacao || '',
  pendenteFinalizacao: item.pendenteFinalizacao ?? false
});

const initialPendenciasResumo = {
  cobPaletizacao: 0,
  valorCobPaletizacao: 0,
  cobEtiquetagem: 0,
  valorCobEtiquetagem: 0,
  cobArmazenamento: 0,
  valorCobArmazenamento: 0,
  semTabelaPreco: 0,
  agendamento: 0,
  saida: 0,
  finalizacao: 0,
  divergencia: 0,
  cadastroIncompleto: 0,
  qtdEtiqueta: 0,
  qtdPalete: 0
};

export const useDepositoNotasListagem = () => {
  const [pendenciaHeader, setPendenciaHeader] = useState('');
  const [resumoPendencias, setResumoPendencias] = useState(initialPendenciasResumo);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [filtros, setFiltros] = useState({
    NumeroNF: '',
    SerieNF: '',
    TipoNota: '',
    DescricaoDevolucao: '',
    MotivoDevolucao: '',
    CNPJCliente: '',
    NomeCliente: '',
    TipoOperacao: '',
    NomeDestinatario: '',
    NomeRemetente: '',
    NomeUsuarioLeitura: '',
    NomeResponsavelComercial: '',
    NomeResponsavelLancamento: '',
    NomeResponsavelFechamento: '',
    ProtocoloPedidoTMSPaletizacao: '',
    ProtocoloPedidoTMSEtiquetagem: '',
    NumeroPedidoTMS: '',
    NumeroCarga: '',
    NumeroPedido: '',
    PlacaVeiculo: '',
    NumeroFrota: '',
    DescricaoModeloVeicular: '',
    InfoMotorista: '',
    ModelosReboque: '',
    FrotasReboque: '',
    PlacasReboque: '',
    DescricaoSituacaoDigitalizacaoCanhoto: '',
    DataAgendamentoPedidoInicio: '',
    DataAgendamentoPedidoFim: '',
    Observacoes: '',
    NomeUsuarioEnvioCanhoto: '',
    DescricaoDivergencia: '',
    DataChegadaInicio: '',
    DataChegadaFim: '',
    DataLeituraInicio: '',
    DataLeituraFim: '',
    DataConferenciaInicio: '',
    DataConferenciaFim: '',
    DataArmazenagemInicio: '',
    DataArmazenagemFim: '',
    DataAgendamentoInicio: '',
    DataAgendamentoFim: '',
    DataSaidaInicio: '',
    DataSaidaFim: '',
    DataEnvioCanhotoInicio: '',
    DataEnvioCanhotoFim: '',
    MudancaTabelaPrecoManual: '',
    CobrancaPaletizacaoRealizada: '',
    CobrancaEtiquetagemRealizada: '',
    CobrancaArmazenagemRealizada: '',
    CobrancaArmazenagemPendente: '',
    ExisteDivergencia: '',
    Finalizado: '',
    AgendamentoPendente: '',
    PendenteEntrega: '',
    CanhotoEnviado: '',
    PendenteFinalizacao: '',
    OrdenarPor: '',
    Direcao: ''
  });

  const isMountedRef = useRef(true);
  const didInitialLoadRef = useRef(false);
  const inFlightSignatureRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(
    async (filtrosParaBuscar = filtros, pendenciaHeaderParaBuscar = pendenciaHeader) => {
      if (!isMountedRef.current) return;

      try {
        const baseFilters = normalizeFilters(filtrosParaBuscar);
        const filters = {
          PaginaAtual: paginationState.pageIndex + 1,
          QuantidadePorPagina: paginationState.pageSize,
          ...baseFilters,
          ...(pendenciaHeaderParaBuscar ? { PendenciaHeader: pendenciaHeaderParaBuscar } : {})
        };

        const signature = JSON.stringify({
          filters,
          resumo: baseFilters
        });

        if (inFlightSignatureRef.current === signature) {
          return;
        }

        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        inFlightSignatureRef.current = signature;
        setLoading(true);

        const listPromise = getDepositoNotasFiscais(filters);
        const resumoPromise = getDepositoNotaFiscalResumoPendencias(baseFilters);

        const response = await listPromise;
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;

        const responseData = response?.data || response || {};
        const registros = Array.isArray(responseData.registros) ? responseData.registros : [];

        const dadosProcessados = registros.map(mapDepositoNotaListItem);

        setData(dadosProcessados);
        setTotalRegistros(responseData.totalRegistros || dadosProcessados.length);
        setLoading(false);
        setFilterLoading(false);
        inFlightSignatureRef.current = null;

        resumoPromise
          .then((resumoResponse) => {
            if (!isMountedRef.current || requestId !== requestIdRef.current) return;

            const resumoData = resumoResponse?.data || resumoResponse || {};
            setResumoPendencias({
              cobPaletizacao: resumoData.cobPaletizacao ?? resumoData.CobPaletizacao ?? 0,
              valorCobPaletizacao: resumoData.valorCobPaletizacao ?? resumoData.ValorCobPaletizacao ?? 0,
              cobEtiquetagem: resumoData.cobEtiquetagem ?? resumoData.CobEtiquetagem ?? 0,
              valorCobEtiquetagem: resumoData.valorCobEtiquetagem ?? resumoData.ValorCobEtiquetagem ?? 0,
              cobArmazenamento: resumoData.cobArmazenamento ?? resumoData.CobArmazenamento ?? 0,
              valorCobArmazenamento: resumoData.valorCobArmazenamento ?? resumoData.ValorCobArmazenamento ?? 0,
              semTabelaPreco: resumoData.semTabelaPreco ?? resumoData.SemTabelaPreco ?? 0,
              agendamento: resumoData.agendamento ?? resumoData.Agendamento ?? 0,
              saida: resumoData.saida ?? resumoData.Saida ?? 0,
              finalizacao: resumoData.finalizacao ?? resumoData.Finalizacao ?? 0,
              divergencia: resumoData.divergencia ?? resumoData.Divergencia ?? 0,
              cadastroIncompleto: resumoData.cadastroIncompleto ?? resumoData.CadastroIncompleto ?? 0,
              qtdEtiqueta: resumoData.qtdEtiqueta ?? resumoData.QtdEtiqueta ?? 0,
              qtdPalete: resumoData.qtdPalete ?? resumoData.QtdPalete ?? 0
            });
          })
          .catch((error) => {
            if (!isMountedRef.current || requestId !== requestIdRef.current) return;
            console.error('Erro ao buscar resumo de pendencias de deposito:', error);
          });
      } catch (error) {
        if (!isMountedRef.current) return;
        console.error('Erro ao buscar notas de deposito:', error);
        setLoading(false);
        setFilterLoading(false);
        inFlightSignatureRef.current = null;
      } finally {
        // O loading da tabela encerra junto da resposta da listagem.
      }
    },
    [filtros, paginationState.pageIndex, paginationState.pageSize, pendenciaHeader]
  );

  useEffect(() => {
    if (!didInitialLoadRef.current) return;
    fetchData(filtros, pendenciaHeader);
  }, [paginationState.pageIndex, paginationState.pageSize, pendenciaHeader]);

  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    setLoading(true);
    fetchData();
  }, []);

  const handleFilterApply = useCallback(
    (newFilters) => {
      setFiltros(newFilters);
      setFilterLoading(true);
      setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
      fetchData(newFilters, pendenciaHeader);
    },
    [fetchData, pendenciaHeader]
  );

  const handleLimparFiltros = useCallback(
    (clearedFiltros) => {
      setFiltros(clearedFiltros);
      setPendenciaHeader('');
      setLoading(true);
      setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
      fetchData(clearedFiltros, '');
    },
    [fetchData]
  );

  const handlePendenciaHeaderChange = useCallback(
    (nextPendenciaHeader) => {
      setLoading(true);
      setFilterLoading(true);
      setPendenciaHeader(nextPendenciaHeader || '');
      setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
      fetchData(filtros, nextPendenciaHeader || '');
    },
    [fetchData, filtros]
  );

  const handlePaginationChange = useCallback((next) => {
    setLoading(true);
    setPaginationState((prev) => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  const applyUpdatedNota = useCallback((notaAtualizada) => {
    if (!notaAtualizada?.id) return null;

    const mapped = mapDepositoNotaListItem(notaAtualizada);
    setData((prev) => prev.map((item) => (item.id === mapped.id ? mapped : item)));
    return mapped;
  }, []);

  return {
    data,
    loading,
    filterLoading,
    totalRegistros,
    resumoPendencias,
    pendenciaHeader,
    filtros,
    paginationState,
    fetchData,
    handleFilterApply,
    handleLimparFiltros,
    handlePendenciaHeaderChange,
    handlePaginationChange,
    setPaginationState,
    applyUpdatedNota
  };
};
